import { Star } from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectActiveWorkspaceId,
    setIdRenaming,
    updateTag,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { WorkspacePanelEmptySectionMessage } from "./WorkspacePanelEmptySectionMessage"
import { WorkspaceProjectTab } from "../tab/WorkspaceProjectTab"
import { WorkspaceFolderTab } from "../tab/WorkspaceFolderTab"
import { DroppableSeparator } from "../DroppableSeparator"
import { selectProjectsByTagFilter, selectAllFires } from "features/entity-caching/fires/selectors"
import { useMemo, useRef } from "react"
import { WorkspaceEntityType } from "features/data-management/workspace/types"
import { useTranslation } from "@nova/use-translation"
import { useDragLayer, useDrop } from "react-dnd"
import classNames from "classnames"
import * as Icons from "@nova/component-icons"
import { NovaRouteTree, useRouter } from "pages"
import { useLocation } from "react-router"
import { GetScopedTo } from "@nova/react-router"
import {
    selectAllFoldersById,
    selectSortedSidePanelRootFolders,
    selectFavoritesData,
} from "features/data-management/workspace/context/selectors"
import {
    toggleFavorite,
    moveWorkspaceEntity,
    deleteWorkspaceEntity,
    openWorkspaceShareModal,
} from "features/data-management/workspace/context/thunks"
import { updateFire } from "features/entity-caching/fires/thunks"
import {
    useActivateProject,
    useCreateFolder,
} from "features/data-management/workspace/context/navigation"

export const WorkspacePanelProjectsAndFolders = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const allFoldersById = useAppSelector(selectAllFoldersById)
    const allProjectsById = useAppSelector(selectAllFires)
    const sortedSidePanelRootFolders = useAppSelector(selectSortedSidePanelRootFolders)
    const { sortedFavoriteIds, favoriteIds } = useAppSelector(selectFavoritesData)
    const isFavorite = (id: string) => favoriteIds.has(id)
    const activateProject = useActivateProject()
    const createFolder = useCreateFolder()

    const handleToggleFavorite = (id: string) => dispatch(toggleFavorite(id))
    const handleMove = (id: string, type: WorkspaceEntityType, newParentFolderId?: string) =>
        dispatch(moveWorkspaceEntity({ id, type, newParentFolderId }))
    const handleDelete = (id: string, name: string, type: WorkspaceEntityType) =>
        dispatch(deleteWorkspaceEntity({ id, name, type }))
    const handleOpenShareModal = (id: string) => dispatch(openWorkspaceShareModal(id))
    const handleBeginRenaming = (id: string) => dispatch(setIdRenaming(id))
    const handleConfirmRenaming = (id: string, name: string, type: WorkspaceEntityType) => {
        if (type === WorkspaceEntityType.Folder) {
            dispatch(updateTag({ id, updates: { name } }))
        } else {
            dispatch(updateFire({ id, updates: { name } }))
        }
        dispatch(setIdRenaming(undefined))
    }
    const handleCancelRenaming = () => dispatch(setIdRenaming(undefined))

    const rootLevelProjects = useAppSelector(selectProjectsByTagFilter([]))

    const rootLevelFolderIds = useMemo(
        () => sortedSidePanelRootFolders?.map((folder) => folder.id) || [],
        [sortedSidePanelRootFolders]
    )

    const rootLevelProjectIds = useMemo(
        () => rootLevelProjects.map((project) => project.id),
        [rootLevelProjects]
    )

    const [{ isOver, canDrop }, dropRef] = useDrop(
        () => ({
            accept: [WorkspaceEntityType.Folder, WorkspaceEntityType.Project],
            drop: ({ id }) => handleToggleFavorite(id),
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop(),
            }),
            canDrop: ({ id }: { id: string }) => !isFavorite(id),
        }),
        [favoriteIds]
    )

    const anythingIsDragging = useDragLayer((monitor) => {
        const type = monitor.getItemType()
        const isAcceptableType =
            type === WorkspaceEntityType.Folder || type === WorkspaceEntityType.Project
        return monitor.isDragging() && isAcceptableType
    })

    const favoritesDropRef = useRef<HTMLDivElement>(null)
    dropRef(favoritesDropRef)

    if (!workspaceId) return null

    return (
        <>
            <div>
                {sortedFavoriteIds.length > 0 || !anythingIsDragging ? (
                    <>
                        <div className="relative">
                            <DroppableSeparator
                                acceptedType={[
                                    WorkspaceEntityType.Folder,
                                    WorkspaceEntityType.Project,
                                ]}
                                idBelow={
                                    sortedFavoriteIds.length > 0 ? sortedFavoriteIds[0] : undefined
                                }
                            />
                        </div>
                        <WorkspaceExplorerNestedSidebarSectionHeader
                            to={{ path: "/explore/favorites" }}
                            title="Favorites"
                            handleDrop={({ id }) => handleToggleFavorite(id)}
                            draggedItemCanDrop={(item) => !isFavorite(item.id)}
                        />
                    </>
                ) : (
                    <div
                        ref={favoritesDropRef}
                        className={classNames(
                            "w-full h-[4.5rem] rounded-md border flex items-center justify-center",
                            {
                                "border-dashed": !isOver,
                                "border-blue-500": canDrop,
                                "border-red-500": !canDrop,
                            }
                        )}
                    >
                        <h5 className="text-gray-500 select-none">
                            {t("Drop to add to Favorites")}
                        </h5>
                    </div>
                )}

                {sortedFavoriteIds.map((id, i) => {
                    const key = `fav_${id}`
                    const folder = allFoldersById[id]
                    const project = allProjectsById[id]

                    const idAbove = id
                    const idBelow =
                        i + 1 < sortedFavoriteIds.length ? sortedFavoriteIds[i + 1] : undefined

                    return (
                        <div key={key} className="relative">
                            {folder ? (
                                <WorkspaceFolderTab
                                    folder={folder}
                                    categoryKey={key}
                                    tagChain={[folder.id]}
                                    isFavorite={isFavorite}
                                    handleMove={handleMove}
                                    handleCreateFolder={(insideOfId) =>
                                        createFolder({ insideOfId })
                                    }
                                    handleBeginRenaming={handleBeginRenaming}
                                    handleConfirmRenaming={handleConfirmRenaming}
                                    handleCancelRenaming={handleCancelRenaming}
                                    handleOpenShareModal={handleOpenShareModal}
                                    handleToggleFavorite={(id) => handleToggleFavorite(id).unwrap()}
                                    handleDelete={handleDelete}
                                    subFolders={folder.subTags}
                                    shouldPopulateChildren={false}
                                    depth={0}
                                    extraCanDropCondition={(item) => !isFavorite(item.id)}
                                />
                            ) : project ? (
                                <WorkspaceProjectTab
                                    project={project}
                                    categoryKey={key}
                                    isFavorite={isFavorite(id)}
                                    handleActivateProject={activateProject}
                                    handleMove={handleMove}
                                    handleBeginRenaming={handleBeginRenaming}
                                    handleConfirmRenaming={handleConfirmRenaming}
                                    handleCancelRenaming={handleCancelRenaming}
                                    handleOpenShareModal={handleOpenShareModal}
                                    handleToggleFavorite={(id) => handleToggleFavorite(id).unwrap()}
                                    handleDelete={handleDelete}
                                />
                            ) : null}
                            {(folder || project) && (
                                <DroppableSeparator
                                    acceptedType={[
                                        WorkspaceEntityType.Folder,
                                        WorkspaceEntityType.Project,
                                    ]}
                                    idAbove={idAbove}
                                    idBelow={idBelow}
                                />
                            )}
                        </div>
                    )
                })}

                {sortedFavoriteIds.length === 0 && !anythingIsDragging && (
                    <WorkspacePanelEmptySectionMessage title="To add to the side bar" Icon={Star} />
                )}
            </div>

            <div>
                <WorkspaceExplorerNestedSidebarSectionHeader
                    to={{ path: "/explore/ws" }}
                    title="Workspace"
                    draggedItemCanDrop={(item) =>
                        !rootLevelFolderIds.includes(item.id) &&
                        !rootLevelProjectIds.includes(item.id)
                    }
                    handleDrop={({ id, type }) => handleMove(id, type)}
                />
                {sortedSidePanelRootFolders.map((folder) => {
                    const key = `all_${folder.id}`
                    return (
                        <WorkspaceFolderTab
                            key={key}
                            folder={folder}
                            categoryKey={key}
                            tagChain={[folder.id]}
                            isFavorite={isFavorite}
                            handleMove={handleMove}
                            handleCreateFolder={(insideOfId) => createFolder({ insideOfId })}
                            handleBeginRenaming={handleBeginRenaming}
                            handleConfirmRenaming={handleConfirmRenaming}
                            handleCancelRenaming={handleCancelRenaming}
                            handleOpenShareModal={handleOpenShareModal}
                            handleToggleFavorite={(id) => handleToggleFavorite(id).unwrap()}
                            handleDelete={handleDelete}
                            subFolders={folder.subTags}
                            shouldPopulateChildren={true}
                            depth={0}
                        />
                    )
                })}
            </div>
        </>
    )
}

const WorkspaceExplorerNestedSidebarSectionHeader = ({
    title,
    to,
    draggedItemCanDrop = () => false,
    handleDrop,
}: {
    title: string
    to: GetScopedTo<NovaRouteTree, "/workspace/:workspaceId">
    draggedItemCanDrop?: (item: { id: string; type: WorkspaceEntityType }) => boolean
    handleDrop?: ({ id, type }: { id: string; type: WorkspaceEntityType }) => void
}) => {
    const router = useRouter("/workspace/:workspaceId")
    const url = router.preview(to)
    const location = useLocation()
    const isActive = useMemo(() => url === location.pathname, [location.pathname, url])
    const [{ isOver, canDrop }, dropRef] = useDrop(
        () => ({
            accept: [WorkspaceEntityType.Folder, WorkspaceEntityType.Project],
            drop: handleDrop,
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop(),
            }),
            canDrop: draggedItemCanDrop,
        }),
        [draggedItemCanDrop, handleDrop]
    )
    const anythingIsDragging = useDragLayer((monitor) => {
        const type = monitor.getItemType()
        const isAcceptableType =
            type === WorkspaceEntityType.Folder || type === WorkspaceEntityType.Project
        return monitor.isDragging() && isAcceptableType
    })
    const headerDropRef = useRef<HTMLDivElement>(null)
    dropRef(headerDropRef)
    return (
        <router.Link to={to}>
            <div
                className={classNames(
                    "group relative w-full p-2 rounded-lg border transition-colors flex justify-between items-center",
                    {
                        "border-transparent": !isOver,
                        "border-blue-500": canDrop && anythingIsDragging && isOver,
                        "border-red-500": !canDrop && anythingIsDragging && isOver,
                        "opacity-20": !canDrop && anythingIsDragging,
                        "hover:bg-white-100": !isActive,
                        "bg-white-200": isActive,
                    }
                )}
            >
                <div
                    className={classNames("transition-colors", {
                        "text-gray-100 group-hover:text-white": !isActive,
                        "text-white": isActive,
                    })}
                >
                    {title}
                </div>
                <Icons.ExtraSmallIcon
                    Icon={Icons.ArrowRight}
                    iconClassName={classNames("opacity-0 transition-opacity text-blue-500", {
                        "group-hover:opacity-100": !isActive,
                    })}
                />
                {anythingIsDragging && (
                    <div ref={headerDropRef} className="absolute h-6 w-full top-1.5" />
                )}
            </div>
        </router.Link>
    )
}
